import { zodResolver } from '@hookform/resolvers/zod'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tooltip,
} from '@mui/material'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import CustomButton from '@/components/inputs/CustomButton'
import FloatTextField from '@/components/inputs/FloatTextField'
import type { ResourceLocalConfigFormValues } from '@/features/resource/configuration/utils/resourceLocalConfig'
import {
  FFR_FREQUENCY_THRESHOLDS,
  FFR_MINIMUM_ACTIVATION_DURATIONS,
} from '@/features/resource/configuration/utils/resourceLocalConfig'
import { getResourceLocalConfigSchema } from '@/features/resource/schemas'

export type ResourceLocalConfigFormMode = 'edit' | 'create'

type ResourceLocalConfigFormProps = {
  defaultValues: ResourceLocalConfigFormValues
  mode: ResourceLocalConfigFormMode
  /** Battery picker is shown only in create-entry mode (no existing `resources[]` entry). */
  needsBatteryPicker: boolean
  batteryOptions: readonly string[]
  isSaving: boolean
  /** Rendered ABOVE the form fields inside the left column (e.g. the LC-94 BindingPanel).
   * Shares the form's FormProvider so any inline edit hooks work. */
  headerPanels?: ReactNode
  /** Rendered as a sticky right-column inside the FormProvider so the operating envelope chart can
   * subscribe to live form state; ConfigVersionInfo + ResourceRestartStatusPanel also live here so
   * the whole right-side viewport is one cohesive sticky stack. Omit for single-column layouts. */
  sidebar?: ReactNode
  onSave: (values: ResourceLocalConfigFormValues) => void
  onSaveAndRestart: (values: ResourceLocalConfigFormValues) => void
}

export function ResourceLocalConfigForm({
  defaultValues,
  mode,
  needsBatteryPicker,
  batteryOptions,
  isSaving,
  headerPanels,
  sidebar,
  onSave,
  onSaveAndRestart,
}: Readonly<ResourceLocalConfigFormProps>) {
  const { t } = useTranslation()
  const form = useForm<ResourceLocalConfigFormValues>({
    defaultValues,
    resolver: zodResolver(
      getResourceLocalConfigSchema(t, {
        requireMaxPower: mode === 'create',
        requireBatteryRef: needsBatteryPicker,
      }),
    ),
  })

  const { handleSubmit, control } = form
  // Trickle section is gated on its enable toggle: when OFF, its fields are disabled (values stay
  // visible). Saving while OFF clears the targetSoc* leaves; setpoint/rampSpeed/restSetpoint
  // round-trip (see applyLocalConfig).
  const trickleEnabled = useWatch({ control, name: 'targetSocEnabled' }) as boolean

  return (
    <FormProvider {...form}>
      <Stack alignItems="flex-start" direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Box sx={{ flex: 6, width: '100%', maxWidth: { md: 640 }, minWidth: 0 }}>
          <Paper sx={{ p: 3 }}>
            <Stack direction="column" spacing={3}>
              {headerPanels}
              {/* `noValidate`: RHF + Zod own validation. Without it, native HTML `required` blocks
               * submission before the resolver runs, so required fields never surface the inline
               * red error state (only the browser's native bubble). */}
              <form noValidate aria-label={t('resources.configuration.form.label')} onSubmit={handleSubmit(onSave)}>
                <Stack direction="column" spacing={4}>
                  <Stack alignItems="center" direction="row" spacing={2}>
                    <CustomTypography variant="h2">{t('resources.configuration.form.title')}</CustomTypography>
                    <Chip
                      color={mode === 'create' ? 'warning' : 'default'}
                      label={t(
                        mode === 'create'
                          ? 'resources.configuration.form.mode.create'
                          : 'resources.configuration.form.mode.edit',
                      )}
                      size="small"
                    />
                  </Stack>

                  {needsBatteryPicker && <BatteryPicker options={batteryOptions} />}

                  <Section
                    titleKey="resources.configuration.form.sections.operational_limits.title"
                    tooltipKey="resources.configuration.form.sections.operational_limits.tooltip"
                  >
                    <NumericField max={100} name="maxSocPercent" step={1} unit="%" />
                    <NumericField max={100} name="minSocPercent" step={1} unit="%" />
                  </Section>

                  <Section
                    titleKey="resources.configuration.form.sections.general.title"
                    tooltipKey="resources.configuration.form.sections.general.tooltip"
                  >
                    <NumericField
                      name="maxChargePowerKw"
                      numberOfDecimals={3}
                      required={mode === 'create'}
                      step={50}
                      unit="kW"
                    />
                    <NumericField
                      name="maxDischargePowerKw"
                      numberOfDecimals={3}
                      required={mode === 'create'}
                      step={50}
                      unit="kW"
                    />
                    <NumericField
                      name="chargeSpeedKwPerSecond"
                      numberOfDecimals={3}
                      step={0.1}
                      tooltipKey="resources.configuration.form.fields.ramp_speed_tooltip"
                      unit="kW/s"
                    />
                    <NumericField
                      name="dischargeSpeedKwPerSecond"
                      numberOfDecimals={3}
                      step={0.1}
                      tooltipKey="resources.configuration.form.fields.ramp_speed_tooltip"
                      unit="kW/s"
                    />
                    <NumericField name="restSetpointKw" numberOfDecimals={3} step={50} unit="kW" />
                  </Section>

                  <Section
                    titleKey="resources.configuration.form.sections.trickle.title"
                    tooltipKey="resources.configuration.form.sections.trickle.tooltip"
                  >
                    <Controller
                      control={control}
                      name="targetSocEnabled"
                      render={({ field }) => (
                        <Stack alignItems="center" direction="row" spacing={1}>
                          <Switch
                            checked={field.value}
                            // MUI v7 ignores the legacy `inputProps`; use slotProps so the toggle
                            // actually carries an accessible name.
                            slotProps={{
                              input: { 'aria-label': t('resources.configuration.form.fields.targetSocEnabled') },
                            }}
                            onChange={(event) => field.onChange(event.target.checked)}
                          />
                          <CustomTypography>
                            {t('resources.configuration.form.fields.targetSocEnabled')}
                          </CustomTypography>
                        </Stack>
                      )}
                    />
                    {/* All trickle inputs are gated on the section toggle: disabled (but their
                     * values stay visible, consistent with the market-program sections) when OFF.
                     * The switch above stays interactive; form state is preserved either way. */}
                    <FieldDisabledContext.Provider value={!trickleEnabled}>
                      <NumericField max={100} name="targetSocPercent" step={1} unit="%" />
                      <NumericField name="targetSocSecondsHysteresis" step={1} unit="s" />
                      <NumericField name="targetSocSecondsReset" step={1} unit="s" />
                      <NumericField name="trickleChargePowerKw" numberOfDecimals={3} step={50} unit="kW" />
                      <NumericField name="trickleChargeSpeedKwPerSecond" numberOfDecimals={3} step={0.1} unit="kW/s" />
                      <NumericField name="trickleDischargePowerKw" numberOfDecimals={3} step={50} unit="kW" />
                      <NumericField
                        name="trickleDischargeSpeedKwPerSecond"
                        numberOfDecimals={3}
                        step={0.1}
                        unit="kW/s"
                      />
                      <NumericField name="trickleRestSetpointKw" numberOfDecimals={3} step={50} unit="kW" />
                    </FieldDisabledContext.Provider>
                  </Section>

                  <Section
                    titleKey="resources.configuration.form.sections.market_programs.title"
                    tooltipKey="resources.configuration.form.sections.market_programs.tooltip"
                  >
                    <MarketProgramsAccordion />
                  </Section>

                  <Stack direction="row" spacing={2}>
                    <CustomButton disabled={isSaving} type="submit" variant="contained">
                      {t(mode === 'create' ? 'resources.configuration.form.create_button' : 'common.button.save')}
                    </CustomButton>
                    <CustomButton
                      disabled={isSaving}
                      type="button"
                      variant="outlined"
                      onClick={handleSubmit(onSaveAndRestart)}
                    >
                      {t('resources.configuration.form.save_and_restart')}
                    </CustomButton>
                  </Stack>
                </Stack>
              </form>
            </Stack>
          </Paper>
        </Box>
        {sidebar && (
          <Box
            sx={{
              flex: 4,
              width: { xs: '100%', md: 'auto' },
              position: { md: 'sticky' },
              // Shell.tsx's AppTopBar is a normal flex child above the scroll container, not a
              // fixed overlay — so the scroll container's top already sits below the AppBar and
              // the sticky chart pins cleanly at top: 0.
              top: { md: 0 },
              // Anchor the panel to the TOP of its column and let it size to its content. A fixed
              // viewport height here forced the chart to stretch and letterbox (whitespace above the
              // drawing); a content-height panel pins cleanly under the AppBar with no gap on scroll.
              alignSelf: { md: 'flex-start' },
            }}
          >
            {sidebar}
          </Box>
        )}
      </Stack>
    </FormProvider>
  )
}

function Section({
  titleKey,
  tooltipKey,
  children,
}: Readonly<{ titleKey: string; tooltipKey: string; children: ReactNode }>) {
  const { t } = useTranslation()
  return (
    <Stack direction="column" spacing={2}>
      <SectionHeading title={t(titleKey as never)} tooltip={t(tooltipKey as never)} />
      <Stack direction="column" spacing={2}>
        {children}
      </Stack>
    </Stack>
  )
}

export function SectionHeading({ title, tooltip }: Readonly<{ title: string; tooltip: string }>) {
  return (
    <Stack alignItems="center" direction="row" spacing={1}>
      <CustomTypography variant="h3">{title}</CustomTypography>
      <Tooltip title={tooltip}>
        <IconButton aria-label={tooltip} size="small">
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

type MarketProgram = 'fcr' | 'ffr' | 'mfrr' | 'afrr' | 'wholesale'
const MARKET_PROGRAMS: readonly MarketProgram[] = ['fcr', 'ffr', 'mfrr', 'afrr', 'wholesale']

/** Lets NumericField/EnumSelect inside a disabled section (a non-participating market program, or the
 * trickle section when its toggle is OFF) render themselves disabled without prop-drilling. Held
 * values stay visible (parity with the market sections) — only editing is blocked. Defaults to
 * enabled everywhere else. */
const FieldDisabledContext = createContext<boolean>(false)

const PARTICIPATING_FIELD: Record<MarketProgram, keyof ResourceLocalConfigFormValues> = {
  fcr: 'fcrParticipating',
  ffr: 'ffrParticipating',
  mfrr: 'mfrrParticipating',
  afrr: 'afrrParticipating',
  wholesale: 'wholesaleParticipating',
}

const PROGRAM_LEAF_FIELDS: Record<MarketProgram, readonly (keyof ResourceLocalConfigFormValues)[]> = {
  fcr: ['fcrnMaxKw', 'fcrnLowKw', 'fcrnLerKw', 'fcrDUpMaxKw', 'fcrDUpLowKw', 'fcrDDownMaxKw', 'fcrDDownLowKw'],
  ffr: ['ffrFrequencyThreshold', 'ffrMinimumActivationDuration', 'ffrMaxKw', 'ffrLowKw'],
  mfrr: ['mfrrRampTimeSeconds', 'mfrrUpMaxKw', 'mfrrUpLowKw', 'mfrrDownMaxKw', 'mfrrDownLowKw'],
  afrr: ['afrrMaxKw'],
  wholesale: [],
}

export function MarketProgramsAccordion() {
  // Start with every program collapsed — the user expands what they want to look at. The earlier
  // "open whichever program is present" default carried over from the old sub-tab strip and ended
  // up surfacing fields the user didn't ask to see.
  const [expanded, setExpanded] = useState<MarketProgram | null>(null)

  const onToggle = (program: MarketProgram) => () => {
    setExpanded((current) => (current === program ? null : program))
  }

  return (
    <Stack direction="column" spacing={1}>
      {MARKET_PROGRAMS.map((program) => {
        // Programs with no PQ leaves (today: only Wholesale) have nothing to override here —
        // market participation is decided by upstream services, not this form. Render the row
        // with a passive "no overrides available" chip rather than a toggle that would imply
        // the user can opt the resource in or out.
        if (PROGRAM_LEAF_FIELDS[program].length === 0) {
          return <NoOverridesProgramRow key={program} program={program} />
        }
        return (
          <ExpandableProgramAccordion
            key={program}
            expanded={expanded === program}
            program={program}
            onToggleExpanded={onToggle(program)}
          >
            {program === 'fcr' && <FcrFields />}
            {program === 'ffr' && <FfrFields />}
            {program === 'mfrr' && <MfrrFields />}
            {program === 'afrr' && <AfrrFields />}
          </ExpandableProgramAccordion>
        )
      })}
    </Stack>
  )
}

function NoOverridesProgramRow({ program }: Readonly<{ program: MarketProgram }>) {
  const { t } = useTranslation()
  const programLabel = t(`resources.configuration.form.markets.${program}` as never)
  const chipLabel = t('resources.configuration.form.markets.no_overrides')

  // `*Participating` for this program is untouched in the form state — whatever was extracted
  // from on-disk JSON round-trips verbatim through applyLocalConfig. The row is informational
  // only.
  return (
    <Paper
      data-testid={`market-no-overrides-row-${program}`}
      sx={{ px: 2, py: 1.5, border: '1px solid', borderColor: 'divider' }}
      variant="outlined"
    >
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <CustomTypography variant="h4">{programLabel}</CustomTypography>
        <Chip
          color="default"
          data-testid={`market-no-overrides-chip-${program}`}
          label={chipLabel}
          size="small"
          variant="outlined"
        />
      </Stack>
    </Paper>
  )
}

function ExpandableProgramAccordion({
  program,
  expanded,
  onToggleExpanded,
  children,
}: Readonly<{
  program: MarketProgram
  expanded: boolean
  onToggleExpanded: () => void
  children?: ReactNode
}>) {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext<ResourceLocalConfigFormValues>()
  const participatingField = PARTICIPATING_FIELD[program]
  const leafFields = PROGRAM_LEAF_FIELDS[program]
  const participating = useWatch({ control, name: participatingField }) as boolean
  // "Populated" reflects CURRENT form state — confirm dialog fires when the user is about to lose
  // data they've typed in, whether or not it's on disk yet.
  const leafValues = useWatch({
    control,
    name: leafFields as readonly (keyof ResourceLocalConfigFormValues)[],
  }) as Array<unknown>
  const populated = leafValues.some(
    (value) => value !== null && value !== undefined && !(typeof value === 'string' && value.length === 0),
  )
  const [confirmOpen, setConfirmOpen] = useState(false)
  const programLabel = t(`resources.configuration.form.markets.${program}` as never)
  const switchAriaLabel = t('resources.configuration.form.markets.override_defaults_aria', { program: programLabel })

  // a11y: the participation Switch lives in a SIBLING header row above the Accordion. Earlier
  // shape nested the Switch inside AccordionSummary (which renders a role="button" ButtonBase) —
  // interactive-inside-interactive is invalid HTML and confused keyboard/AT focus order. The
  // earlier `onClick`/`onFocus` stopPropagation workarounds existed to defuse that bug; the
  // sibling layout removes the bug entirely so the workarounds are gone too.
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Stack
        alignItems="center"
        data-testid={`market-program-header-${program}`}
        direction="row"
        justifyContent="space-between"
        sx={{ px: 2, py: 1 }}
      >
        <CustomTypography variant="h4">{programLabel}</CustomTypography>
        <Controller
          control={control}
          name={participatingField}
          render={({ field }) => (
            <FormControlLabel
              aria-label={switchAriaLabel}
              control={
                <Switch
                  checked={field.value === true}
                  inputProps={{ 'aria-label': switchAriaLabel }}
                  onChange={(event) => {
                    const next = event.target.checked
                    if (!next && populated) {
                      setConfirmOpen(true)
                      return
                    }
                    field.onChange(next)
                  }}
                />
              }
              label={t('resources.configuration.form.markets.override_defaults')}
            />
          )}
        />
      </Stack>
      <Accordion
        disableGutters
        square
        elevation={0}
        expanded={expanded}
        sx={{
          // Drop the inner Paper styling so the outer Box owns the border/radius — without this
          // we'd nest two borders and the corners wouldn't line up.
          backgroundColor: 'transparent',
          borderTop: '1px solid',
          borderColor: 'divider',
          '&::before': { display: 'none' },
        }}
        onChange={onToggleExpanded}
      >
        <AccordionSummary data-testid={`market-accordion-summary-${program}`} expandIcon={<ExpandMoreIcon />}>
          <CustomTypography color="text.secondary" variant="caption">
            {t('resources.configuration.form.markets.show_overrides')}
          </CustomTypography>
        </AccordionSummary>
        <AccordionDetails>
          {children && (
            <Stack direction="column" spacing={2}>
              <FieldDisabledContext.Provider value={!participating}>{children}</FieldDisabledContext.Provider>
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>
      <ConfirmRemoveProgramDialog
        open={confirmOpen}
        program={program}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setValue(participatingField, false, { shouldDirty: true })
          setConfirmOpen(false)
        }}
      />
    </Box>
  )
}

function ConfirmRemoveProgramDialog({
  open,
  program,
  onCancel,
  onConfirm,
}: Readonly<{ open: boolean; program: MarketProgram; onCancel: () => void; onConfirm: () => void }>) {
  const { t } = useTranslation()
  const programLabel = t(`resources.configuration.form.markets.${program}` as never)
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>
        {t('resources.configuration.form.markets.confirm_remove.title', { program: programLabel })}
      </DialogTitle>
      <DialogContent>
        <CustomTypography>
          {t('resources.configuration.form.markets.confirm_remove.body', { program: programLabel })}
        </CustomTypography>
      </DialogContent>
      <DialogActions>
        <CustomButton variant="outlined" onClick={onCancel}>
          {t('resources.configuration.form.markets.confirm_remove.cancel')}
        </CustomButton>
        <CustomButton variant="contained" onClick={onConfirm}>
          {t('resources.configuration.form.markets.confirm_remove.confirm')}
        </CustomButton>
      </DialogActions>
    </Dialog>
  )
}

/** Caption above the PQ-test capacity inputs in each market-program panel. Live market dispatch
 * uses entirely different signals; these fields only set defaults for PQ-injection tests. */
function PqCaption() {
  const { t } = useTranslation()
  return (
    <CustomTypography color="grey.700" variant="caption">
      {t('resources.configuration.form.markets.pq_caption')}
    </CustomTypography>
  )
}

function FcrFields() {
  return (
    <>
      <PqCaption />
      <NumericField name="fcrnMaxKw" numberOfDecimals={3} step={50} unit="kW" />
      <NumericField name="fcrnLowKw" numberOfDecimals={3} step={50} unit="kW" />
      <NumericField name="fcrnLerKw" numberOfDecimals={3} step={50} unit="kW" />
      <NumericField name="fcrDUpMaxKw" numberOfDecimals={3} step={50} unit="kW" />
      <NumericField name="fcrDUpLowKw" numberOfDecimals={3} step={50} unit="kW" />
      <NumericField name="fcrDDownMaxKw" numberOfDecimals={3} step={50} unit="kW" />
      <NumericField name="fcrDDownLowKw" numberOfDecimals={3} step={50} unit="kW" />
    </>
  )
}

function FfrFields() {
  return (
    <>
      <EnumSelect name="ffrFrequencyThreshold" options={FFR_FREQUENCY_THRESHOLDS} />
      <EnumSelect name="ffrMinimumActivationDuration" options={FFR_MINIMUM_ACTIVATION_DURATIONS} />
      <PqCaption />
      <NumericField name="ffrMaxKw" numberOfDecimals={3} step={50} unit="kW" />
      <NumericField name="ffrLowKw" numberOfDecimals={3} step={50} unit="kW" />
    </>
  )
}

function MfrrFields() {
  return (
    <>
      <NumericField name="mfrrRampTimeSeconds" step={1} unit="s" />
      <PqCaption />
      <NumericField name="mfrrUpMaxKw" numberOfDecimals={3} step={50} unit="kW" />
      <NumericField name="mfrrUpLowKw" numberOfDecimals={3} step={50} unit="kW" />
      <NumericField name="mfrrDownMaxKw" numberOfDecimals={3} step={50} unit="kW" />
      <NumericField name="mfrrDownLowKw" numberOfDecimals={3} step={50} unit="kW" />
    </>
  )
}

function AfrrFields() {
  return (
    <>
      <PqCaption />
      <NumericField name="afrrMaxKw" numberOfDecimals={3} step={50} unit="kW" />
    </>
  )
}

function BatteryPicker({ options }: Readonly<{ options: readonly string[] }>) {
  const { t } = useTranslation()
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<ResourceLocalConfigFormValues>()
  const error = errors.batteryRef?.message as string | undefined
  // Picker is always required when shown. MUI renders the standard `*` marker automatically when
  // `<FormControl required>` is set (below); don't append it manually or the field shows `**`.
  const label = t('resources.configuration.form.fields.batteryRef')
  const currentValue = useWatch({ control, name: 'batteryRef' }) as string | null

  // Auto-select when there is exactly one battery option AND the user hasn't picked yet. Two or
  // more options stay null so the user makes an explicit choice. Don't mark the form dirty —
  // this is a programmatic default, not a user edit, and a future "discard unsaved changes"
  // prompt would otherwise fire on first render.
  useEffect(() => {
    if (currentValue === null && options.length === 1) {
      setValue('batteryRef', options[0], { shouldDirty: false, shouldValidate: false })
    }
  }, [currentValue, options, setValue])

  return (
    <Controller
      control={control}
      name="batteryRef"
      render={({ field }) => (
        <FormControl fullWidth required error={Boolean(error)}>
          <InputLabel id="battery-ref-label">{label}</InputLabel>
          <Select
            label={label}
            labelId="battery-ref-label"
            value={field.value ?? ''}
            onChange={(event) => field.onChange(event.target.value || null)}
          >
            {options.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
          {/* When the picker has a validation error we surface it via the SAME FormHelperText slot
           * MUI wires to the Select via aria-describedby — that way the error is announced by
           * screen readers as part of the field. The help text is the fallback content. */}
          <FormHelperText error={Boolean(error)} role={error ? 'alert' : undefined}>
            {error ?? t('resources.configuration.form.battery_picker_help')}
          </FormHelperText>
        </FormControl>
      )}
    />
  )
}

type NumericFieldName = {
  [K in keyof ResourceLocalConfigFormValues]: ResourceLocalConfigFormValues[K] extends number | null ? K : never
}[keyof ResourceLocalConfigFormValues]

type NumericFieldProps = {
  name: NumericFieldName
  unit: string
  step: number
  numberOfDecimals?: number
  required?: boolean
  /** Upper bound (percentage fields pass 100). Omit for unbounded-above quantities. */
  max?: number
  /** Optional i18n key for an info-icon tooltip rendered as the input's end-adornment. */
  tooltipKey?: string
}

function NumericField({
  name,
  unit,
  step,
  numberOfDecimals = 2,
  required,
  max,
  tooltipKey,
}: Readonly<NumericFieldProps>) {
  const { t } = useTranslation()
  const { control } = useFormContext<ResourceLocalConfigFormValues>()
  const disabled = useContext(FieldDisabledContext)

  // FloatTextField forwards `required` to the underlying MUI TextField, which renders the standard
  // trailing `*` on its own. Don't append a manual `*` here or the input shows `**` (same trap as
  // BatteryPicker).
  const label = t(`resources.configuration.form.fields.${name}` as never)
  const tooltipText = tooltipKey ? t(tooltipKey as never) : null

  // Controller registers the field so validation follows the form's reValidateMode — after the first
  // submit, a corrected value re-validates on change and clears its own error. fieldState.error
  // re-renders just this field on each validation pass.
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const error = fieldState.error?.message
        return (
          <FloatTextField
            error={Boolean(error)}
            initialValue={field.value === null || field.value === undefined ? '' : String(field.value)}
            // Clamp bounds mirror the schema (every field is non-negative; percentage fields also cap
            // at 100). Clamping at the input keeps an out-of-range value out of form state, so it can't
            // sit on a disabled field and block save with an error the user can't reach to fix.
            max={max}
            min={0}
            numberOfDecimals={numberOfDecimals}
            step={step}
            textFieldProps={{
              label,
              required,
              disabled,
              fullWidth: true,
              helperText: error,
              // The helper text only renders when there's an error; role="alert" makes it a live
              // region so a validation error surfaced on submit is announced, not just reachable.
              slotProps: { formHelperText: { role: 'alert' } },
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end">
                    {unit}
                    {tooltipText !== null && (
                      <Tooltip title={tooltipText}>
                        <IconButton aria-label={tooltipText} edge="end" size="small">
                          <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </InputAdornment>
                ),
              },
            }}
            onValueChange={(value) => field.onChange(value ?? null)}
          />
        )
      }}
    />
  )
}

type EnumFieldName = 'ffrFrequencyThreshold' | 'ffrMinimumActivationDuration'

type EnumSelectProps = {
  name: EnumFieldName
  options: readonly string[]
}

function EnumSelect({ name, options }: Readonly<EnumSelectProps>) {
  const { t } = useTranslation()
  const { control } = useFormContext<ResourceLocalConfigFormValues>()
  const disabled = useContext(FieldDisabledContext)
  const labelId = `${name}-label`
  const label = t(`resources.configuration.form.fields.${name}` as never)

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FormControl fullWidth disabled={disabled}>
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select
            disabled={disabled}
            label={label}
            labelId={labelId}
            value={field.value ?? ''}
            onChange={(event) => field.onChange(event.target.value || null)}
          >
            {options.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
  )
}
